import { Plus } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';

interface AddContextButtonProps {
  /**
   * Opens the shared `@`-mention menu (personas, repos, MCP servers, gateways,
   * Tailscale profiles, network policies). The composer owns the menu; this
   * button just asks it to open.
   */
  onOpen: () => void;
  className?: string;
}

/**
 * "+" trigger that lives in the applied-context row above the composer and
 * opens the same `@`-mention menu the composer does, so attachments and context
 * have one obvious entry point. It owns no menu state — dashed to read as an
 * "add" affordance, matching the config chips it replaces.
 */
export function AddContextButton({ onOpen, className }: AddContextButtonProps) {
  return (
    <Button
      variant="outline"
      aria-label="Add context"
      data-testid="add-context-button"
      onClick={onOpen}
      className={cn(
        'h-auto gap-1 rounded-full border-dashed border-border/70 bg-card/40 py-1 pl-1.5 pr-2 text-2xs font-normal text-muted-foreground shadow-none',
        'hover:border-border/90 hover:bg-accent hover:text-foreground',
        'dark:border-border/70 dark:bg-card/40 dark:hover:bg-accent',
        className,
      )}
    >
      <Plus className="size-3 shrink-0" />
      <span>Add context</span>
    </Button>
  );
}
